<template>
  <Dialog
    :visible="modelValue"
    :header="header"
    :modal="true"
    :style="{ width: '600px' }"
    @update:visible="handleClose"
  >
    <div class="flex flex-col gap-2">
      <div class="reorder-hint">
        <i class="pi pi-arrows-alt text-gray-400 mr-2" />
        <span class="text-sm text-gray-500">Перетащите элементы для изменения порядка</span>
      </div>

      <div
        v-for="(item, index) in localItems"
        :key="item.id"
        class="reorder-item"
        draggable="true"
        @dragstart="onDragStart($event, index)"
        @dragover="onDragOver($event)"
        @drop="onDrop($event, index)"
      >
        <i class="pi pi-bars drag-handle" />
        <img :src="item.imageUrl" class="reorder-image" />
        <div class="reorder-info">
          <div class="reorder-title">{{ item.title }}</div>
          <div v-if="item.subtitle" class="reorder-subtitle">{{ truncateText(item.subtitle, 40) }}</div>
        </div>
        <Badge :value="index + 1" severity="info" />
      </div>
    </div>

    <template #footer>
      <Button label="Отмена" severity="secondary" outlined @click="handleClose" />
      <Button
        label="Сохранить порядок"
        @click="handleSave"
        :loading="saving"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Badge from "primevue/badge";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  header: { type: String, default: "Изменить порядок" }
});

const emit = defineEmits(["update:modelValue", "save"]);

const saving = ref(false);
const dragStartIndex = ref(null);
const localItems = ref([]);

function truncateText(str, length) {
  if (!str) return '';
  return str.length > length ? str.slice(0, length) + '...' : str;
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      localItems.value = [...props.items];
    }
  },
  { immediate: true }
);

watch(
  () => props.items,
  (newItems) => {
    if (props.modelValue) {
      localItems.value = [...newItems];
    }
  }
);

function onDragStart(event, index) {
  dragStartIndex.value = index;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/plain", index);
}

function onDragOver(event) {
  event.preventDefault();
  event.dataTransfer.dropEffect = "move";
}

function onDrop(event, dropIndex) {
  event.preventDefault();
  const newItems = [...localItems.value];
  const [draggedItem] = newItems.splice(dragStartIndex.value, 1);
  newItems.splice(dropIndex, 0, draggedItem);
  localItems.value = newItems;
}

function handleClose() {
  if (saving.value) return;
  emit("update:modelValue", false);
}

function handleSave() {
  const updatedOrders = {};
  localItems.value.forEach((item, idx) => {
    updatedOrders[item.id] = idx;
  });
  saving.value = true;
  emit("save", updatedOrders, {
    resolve() {
      saving.value = false;
      emit("update:modelValue", false);
    },
    reject() {
      saving.value = false;
    },
  });
}
</script>

<style scoped>
.reorder-hint {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.reorder-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  cursor: grab;
  transition: all 0.2s;
}

.reorder-item:active {
  cursor: grabbing;
}

.reorder-item:hover {
  background: #fafafa;
  border-color: #c41217;
  transform: translateX(4px);
}

.drag-handle {
  color: #999;
  font-size: 18px;
  cursor: grab;
}

.reorder-image {
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 8px;
}

.reorder-info {
  flex: 1;
  min-width: 0;
}

.reorder-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.reorder-subtitle {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 700px) {
  .reorder-item {
    flex-wrap: wrap;
  }

  .reorder-info {
    width: 100%;
  }
}
</style>
